import {CommonModule} from '@angular/common';
import {Component, computed, EventEmitter, Input, Output, Signal, signal, ViewChild} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {FormsModule} from '@angular/forms';
import {MatOption} from '@angular/material/autocomplete';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatError, MatFormField} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatSelect} from '@angular/material/select';
import {MatTable, MatTableModule} from '@angular/material/table';
import {MatTooltipModule} from '@angular/material/tooltip';
import {PermissionRow, PermissionsContext} from '@app/permissions/permissions.mapper';
import {DatasetRoleOptions} from '@shared/labels/dataset-role';
import {ProjectRoleOptions} from '@shared/labels/project-role';

@Component({
  selector: 'app-permissions-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIcon,
    MatTableModule,
    MatSelect,
    MatOption,
    MatInput,
    MatFormField,
    MatError,
    MatCheckboxModule,
    MatTooltipModule,
  ],
  templateUrl: './permissions-table.component.html',
  styleUrls: ['./permissions-table.component.scss'],
})
export class PermissionsTableComponent {
  @Input() rows: Signal<PermissionRow[]> = signal([]);
  @Input() context!: PermissionsContext;
  rolesAvailable: Signal<{label: string; value: string}[]> = computed(() => {
    switch (this.context?.type) {
      case 'project':
        return Object.keys(ProjectRoleOptions).map(roleId => ({value: roleId, label: ProjectRoleOptions[roleId].text}));
      case 'dataset':
        return Object.keys(DatasetRoleOptions).map(roleId => ({value: roleId, label: DatasetRoleOptions[roleId].text}));
      default:
        return [];
    }
  });

  @Output() save = new EventEmitter<PermissionRow>();
  @Output() delete = new EventEmitter<PermissionRow>();
  @Output() useSync = new EventEmitter<boolean>();
  @Output() changeOrgAccess = new EventEmitter<boolean>();

  @Input() useEmailDropdown = false;
  @Input() availableEmails: string[] = [];

  editingRow = signal<string | null>(null);
  addingRow = computed(() => !!this.newRow());
  newRow = signal<PermissionRow | null>(null);

  @ViewChild(MatTable) table: MatTable<PermissionRow>;

  get displayedColumns() {
    return this.context?.editable ? ['email', 'role', 'edit', 'delete'] : ['email', 'role'];
  }

  readonly tableRoles = toObservable(
    computed(() => {
      const base = this.rows();
      return this.newRow() ? [...base, this.newRow()!] : base;
    }),
  );

  get showActions() {
    return this.context?.editable || this.context?.showEditButton;
  }

  startAdd() {
    if (this.newRow()) return;
    this.editingRow.set(null);

    const firstRole = this.rolesAvailable()[0];

    this.newRow.set({
      email: '',
      roleId: firstRole?.value ?? '',
      roleLabel: firstRole?.label ?? '',
    });
  }

  onEdit(row: PermissionRow) {
    this.newRow.set(null);
    this.editingRow.set(row.email);
  }

  onSave(row: PermissionRow) {
    this.save.emit(row);
    this.resetState();
  }

  onDelete(row: PermissionRow) {
    this.delete.emit(row);
  }

  onChangeSync(newState: boolean) {
    this.useSync.emit(newState);
  }

  onChangeOrgAccess(newState: boolean) {
    this.changeOrgAccess.emit(newState);
  }

  cancel(row: PermissionRow) {
    this.editingRow.set(null);
    if (this.newRow() === row) {
      this.newRow.set(null);
    }
  }

  private resetState() {
    this.editingRow.set(null);
    this.newRow.set(null);
  }
}
